<script setup>
import { ref, computed, watch, onBeforeMount, defineProps } from "vue";
import { useTerritorioStore } from "@/store/territorio";
import { useTerritoriosStore } from "@/store/territorios";
import { useRouterStore } from "@/store/router";
import { useTrabajoStore } from "@/store/useTrabajoStore";
import { useSalidaStore } from "@/store/useSalidaStore";
import { useTurnoSalida } from "@/composables/useTurnoSalida";
import { TURNO_OPTIONS } from "@/utils/turno";
import MapByName from "@/components/MapByName/MapByName.vue";
import MenuConductores from "@/components/MenuConductores.vue";
import ToggleButton from 'primevue/togglebutton';
import SelectButton from 'primevue/selectbutton';
import Calendar from 'primevue/calendar';
import Button from 'primevue/button';

const route = useRouterStore();
const terr = useTerritorioStore();
const trabajo = useTrabajoStore();
const territorios = useTerritoriosStore();
const salida = useSalidaStore();
const { buscarTurnoSalida } = useTurnoSalida();

const conductorName = (val) => {
  if (!val) return "";
  const found = salida.conductores.find((c) => c.id === val);
  return found ? found.name : val;
};

const props = defineProps({ isMobile: Boolean })

const viewForm = ref(true)

const territorioKey = computed(() => terr.uuid)

const doc = computed(() => trabajo.data[territorioKey.value] || {})

const registrosOrdenados = computed(() =>
  [...(doc.value.registros || [])].sort((a, b) => b.fecha - a.fecha)
)

const asignados = computed(() => {
  const data = {}
  for (const grupo of territorios.asignadosActivos) {
    for (const nombre of grupo.territorios) {
      data[nombre] = grupo
    }
  }
  return data
})

const mzNumbers = () => {
  const obj = {}
  for (const i in terr.mzNumbers) {
    if (i == 0) continue
    obj[i] = false
  }
  return obj
}

const worked = ref(mzNumbers())
const date = ref(new Date())
const conductor = ref(null)
const notas = ref('')
const showNew = ref(false)
const editRegistroId = ref(null)
const turno = ref(null)
const turnoDetectadoHora = ref(null)
const turnoError = ref(false)

onBeforeMount(() => {
  if (!terr.numero) route.$patch({ page: 'listaTerritorio' })
})

const detectarTurno = () => {
  const detectado = buscarTurnoSalida(territorio_nombre(), date.value)
  if (detectado) {
    turno.value = detectado.turno
    turnoDetectadoHora.value = detectado.hour
  } else {
    turno.value = null
    turnoDetectadoHora.value = null
  }
  turnoError.value = false
}

watch(date, () => { if (showNew.value) detectarTurno() })

const newRegister = () => {
  showNew.value = true
  editRegistroId.value = null
  date.value = new Date()
  conductor.value = null
  notas.value = ''
  worked.value = mzNumbers()
  detectarTurno()
}

const edit = (registro) => {
  editRegistroId.value = registro.id
  showNew.value = true
  date.value = new Date(registro.fecha)
  const c = salida.conductores.find((c) => c.id === registro.conductor || c.name === registro.conductor)
  conductor.value = c?.id ?? registro.conductor
  notas.value = registro.notas
  turno.value = registro.turno ?? null
  turnoDetectadoHora.value = null
  turnoError.value = false
  worked.value = mzNumbers()
  for (const k in worked.value) {
    if (registro.manzanas.includes(Number(k))) worked.value[k] = true
  }
}

const register = () => {
  if (!turno.value) {
    turnoError.value = true
    return
  }

  const manzanas = Object.keys(worked.value).filter(k => worked.value[k]).map(Number)
  Object.keys(worked.value).forEach(k => { worked.value[k] = false })

  const params = { fecha: date.value.getTime(), conductor: conductor.value, manzanas, notas: notas.value, turno: turno.value }

  if (editRegistroId.value) {
    trabajo.editarRegistro(territorioKey.value, editRegistroId.value, params)
  } else {
    trabajo.agregarRegistro(territorioKey.value, params)
  }

  showNew.value = false
  editRegistroId.value = null
}

const selectAllOption = ref(0)
const selectAll = () => {
  const nv = selectAllOption.value + 1
  const option = nv <= 3 ? nv : 0
  selectAllOption.value = option
  const p50 = terr.mzNumbers.length / 2

  for (const i in worked.value) {
    if (option === 1) worked.value[i] = true
    else if (option === 2) worked.value[i] = Number(i) < p50
    else if (option === 3) worked.value[i] = Number(i) >= p50
    else worked.value[i] = false
  }
}

const territorio_nombre = () => `${terr.zona || ''}${terr.numero}`

const unlink = () => {
  const name = territorio_nombre()
  const grupo = asignados.value[name]
  territorios.$patch({
    asignados: territorios.asignados.map(item => {
      if (item.id === grupo.id) {
        item.territorios = item.territorios.filter(t => t !== name)
      }
      return item
    })
  })
}
</script>
<template>
  <div class="grid grid-nogutter w-full pt-2 text-left">
    <div class="col-12 flex justify-content-between mb-2">
      <div class="flex">
        <h1 class="m-0">{{ territorio_nombre() }}</h1>
      </div>
      <div v-if="props.isMobile">
        <Button icon="pi pi-map"
          :label="viewForm ? 'Ver Mapa' : 'Ocultar Mapa'"
          @click="viewForm = !viewForm" />
      </div>
    </div>

    <div class="col-12 md:col-4 pl-1" v-show="(viewForm && props.isMobile) || !props.isMobile">
      <div class="pt-2">{{ terr.referencia }}</div>
      <div class="pt-2">{{ terr.notas }}</div>

      <div v-if="asignados[territorio_nombre()]" class="text-sm flex gap-2">
        <span class="text-accent">Asignado a {{ asignados[territorio_nombre()].assignedTo }}</span>
        <a href="#" @click="unlink()">Liberar</a>
      </div>

      <div class="pt-2">
        <h3>Detalle</h3>
        <p class="pb-2">Última actividad: <span>{{ doc.ultimaActividad ? trabajo.getDate(doc.ultimaActividad) : "-" }}</span></p>
        <p>Última vez completado: <span>{{ doc.ultimaCompletacion ? trabajo.getDate(doc.ultimaCompletacion) : "-" }}</span></p>
        <p class="text-sm">{{ terr.tipo === 'default' ? 'Manzanas' : 'Rutas' }} Pendientes: <span>{{ (doc.manzanasPendientes || []).join(", ") }}</span></p>
        <p class="text-xs mt-2">Veces completado: <span>{{ doc.vecesCompletado }}</span></p>
      </div>

      <div v-if="showNew">
        <h3>Nuevo Informe</h3>
        <div class="pb-3">
          <Calendar v-model="date" showIcon dateFormat="dd/mm/yy" />
        </div>
        <div class="pb-3">
          <MenuConductores :dropdown="true" v-model="conductor" />
        </div>
        <div class="pb-3">
          <div class="pb-1">Horario</div>
          <SelectButton v-model="turno" :options="TURNO_OPTIONS" optionLabel="label" optionValue="value" @change="turnoError = false" />
          <div v-if="turnoDetectadoHora" class="text-xs mt-1">
            Detectado de la salida de las {{ turnoDetectadoHora }}
          </div>
          <div v-else-if="turnoError" class="text-xs mt-1" style="color: var(--danger, #ff3b30)">
            No se encontró una salida con horario fijado para este territorio: elegí el horario en el que se predicó.
          </div>
        </div>
        <div class="pb-2">
          Seleccione las {{ terr.tipo === 'default' ? 'manzanas' : 'rutas' }} que han sido trabajadas
        </div>
        <div class="grid grid-nogutter">
          <div v-for="(check, k) in worked" :key="k" class="mr-2 mb-2">
            <ToggleButton v-model="worked[k]" :onLabel="k" :offLabel="k" />
          </div>
          <div class="mr-2 mb-2">
            <Button label="Todos" @click="selectAll" />
          </div>
        </div>
        <div class="pb-3">
          <Textarea v-model="notas" placeholder="Notas" rows="5" cols="30" />
        </div>
        <div class="pt-3">
          <Button label="Registrar" class="p-button-success" @click="register" />
          <Button label="Cancelar" class="p-button-secondary ml-2" @click="showNew = false" />
        </div>
        <div>
          <h4 class="mt-6 mb-1">Parametros</h4>
          <MenuConductores />
        </div>
      </div>

      <div v-if="!showNew">
        <div class="flex justify-content-between mt-2">
          <h3 class="mt-1">Historial</h3>
          <Button icon="pi pi-plus" size="small" text @click="newRegister()" />
        </div>
        <div class="hist-card">
          <div v-for="registro in registrosOrdenados" :key="registro.id" class="hist-item">
            <div class="flex gap-2 align-items-baseline">
              <div class="py-2 font-bold">{{ registro.manzanas.join(", ") }}</div>
              <Button icon="pi pi-pencil" severity="secondary" size="small" text @click="edit(registro)" />
              <Button icon="pi pi-trash" severity="secondary" size="small" text @click="trabajo.eliminarRegistro(territorioKey, registro.id)" />
            </div>
            <div class="text-xs">{{ trabajo.getDate(registro.fecha) }} - {{ conductorName(registro.conductor) }}</div>
            <div class="text-xs py-2" v-if="registro.notas">{{ registro.notas }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 md:col-8" v-if="(!viewForm && props.isMobile) || !props.isMobile">
      <MapByName :zona="terr.zona || ''" :numero="terr.numero" />
    </div>
  </div>
</template>
<style scoped>
.hist-card {
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-1);
  overflow: hidden;
}
.hist-card > div {
  margin-bottom: var(--space-3);
}
.hist-item {
  padding: var(--space-2) var(--space-3);
  border-bottom: 1px solid var(--separator);
}
.hist-item:last-child {
  border-bottom: none;
}
a {
  color: var(--danger);
  cursor: pointer;
  text-decoration: none;
}
</style>
